import { z } from "zod";
import { LevelDefinition } from "@algoquest/schemas";

/**
 * `workshop` domain DTOs (Master Spec §33/§34/§35/§36, Phase 13).
 *
 * Phase 13 scope ("Workshop V1", re-confirmed against §33/§34/§35/§36
 * before writing any of this): the backend `workshop` domain (author
 * CRUD, publish toggle, admin moderation) plus a non-3D web UI. §36's
 * "Workshop Editor... built using the same rendering foundation [as the
 * Three.js game]" is explicitly Phase 14's job — "Workshop 3D editor" is
 * its own entry in the locked phase list, separate from "Workshop V1".
 * This phase's web UI edits/validates the same `LevelDefinition` JSON
 * textually (a JSON textarea, Zod-validated on submit, with a plain
 * structural/data summary standing in for §36's "Preview/Test" step),
 * never a 3D scene — that visual preview is Phase 14's job. §36's
 * mandate that "the editor and runtime must consume the same level
 * format... do not create separate incompatible level formats" is
 * satisfied by reusing `LevelDefinition` from `@algoquest/schemas`
 * completely unchanged here, not a forked "workshop level" shape.
 *
 * §34 lists "Workshop authoring" only under TEACHER and "Workshop
 * moderation" only under ADMIN — STUDENT has no workshop entry at all —
 * so this phase ships no student-facing browse/play surface. Consuming
 * published+approved workshop levels in actual gameplay is deferred
 * (most naturally to Phase 14, once there is a runtime integration
 * point for it); building that now would be inventing a requirement
 * §34 doesn't state.
 *
 * `LevelDefinition.coordinates` (world/levelInWorld/globalLevelNumber)
 * is reused as-is for workshop content too, per §36. These fields are
 * purely self-contained metadata inside the `workshop_versions
 * .definition` JSONB blob for a workshop level — there is no foreign
 * key or uniqueness constraint tying them to the real `game_levels`
 * table (see `database/schema/workshop.ts` and `levels.ts`), so a
 * workshop author picking, say, `globalLevelNumber: 1` cannot collide
 * with or shadow the official campaign's level 1. This is a deliberate
 * read of §36's "same format" rule as "same JSON shape", not "same
 * database identity space" — the two tables (`game_levels` vs
 * `workshop_versions`) were already kept separate since Phase 00.
 */

export const CreateWorkshopLevelRequestSchema = z.object({
  title: z.string().trim().min(1).max(120),
  definition: LevelDefinition,
});
export type CreateWorkshopLevelRequest = z.infer<typeof CreateWorkshopLevelRequestSchema>;

export const SaveWorkshopVersionRequestSchema = z.object({
  workshopLevelId: z.string().uuid(),
  definition: LevelDefinition,
});
export type SaveWorkshopVersionRequest = z.infer<typeof SaveWorkshopVersionRequestSchema>;

export const SetWorkshopPublishedRequestSchema = z.object({
  workshopLevelId: z.string().uuid(),
  published: z.boolean(),
});
export type SetWorkshopPublishedRequest = z.infer<typeof SetWorkshopPublishedRequestSchema>;

export const ModerationDecisionRequestSchema = z.object({
  workshopLevelId: z.string().uuid(),
  decision: z.enum(["approve", "reject"]),
});
export type ModerationDecisionRequest = z.infer<typeof ModerationDecisionRequestSchema>;

export const WorkshopLevelDTOSchema = z.object({
  id: z.string().uuid(),
  authorId: z.string().uuid(),
  title: z.string(),
  published: z.boolean(),
  moderationApproved: z.boolean(),
  versionNumber: z.number().int().positive(),
  definition: LevelDefinition,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type WorkshopLevelDTO = z.infer<typeof WorkshopLevelDTOSchema>;

/**
 * Admin moderation queue rows need author identity (Master Spec §34
 * ADMIN "Workshop moderation") — a separate schema rather than
 * `WorkshopLevelDTOSchema.extend(...)`, matching this codebase's
 * established caution around unverified Zod/drizzle API surface (see
 * `classes.repository.ts`'s two-query-merge doc comment): `.extend()`
 * is standard Zod v3, but every field here is written out explicitly
 * rather than reaching for API this codebase hasn't already exercised.
 */
export const ModerationQueueItemDTOSchema = z.object({
  id: z.string().uuid(),
  authorId: z.string().uuid(),
  authorEmail: z.string().email(),
  authorDisplayName: z.string(),
  title: z.string(),
  published: z.boolean(),
  moderationApproved: z.boolean(),
  versionNumber: z.number().int().positive(),
  definition: LevelDefinition,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ModerationQueueItemDTO = z.infer<typeof ModerationQueueItemDTOSchema>;

export const CreateWorkshopLevelResponseSchema = z.object({ level: WorkshopLevelDTOSchema });
export type CreateWorkshopLevelResponse = z.infer<typeof CreateWorkshopLevelResponseSchema>;

export const ListMyWorkshopLevelsResponseSchema = z.object({ levels: z.array(WorkshopLevelDTOSchema) });
export type ListMyWorkshopLevelsResponse = z.infer<typeof ListMyWorkshopLevelsResponseSchema>;

export const SaveWorkshopVersionResponseSchema = z.object({ level: WorkshopLevelDTOSchema });
export type SaveWorkshopVersionResponse = z.infer<typeof SaveWorkshopVersionResponseSchema>;

export const SetWorkshopPublishedResponseSchema = z.object({ level: WorkshopLevelDTOSchema });
export type SetWorkshopPublishedResponse = z.infer<typeof SetWorkshopPublishedResponseSchema>;

export const ListModerationQueueResponseSchema = z.object({ items: z.array(ModerationQueueItemDTOSchema) });
export type ListModerationQueueResponse = z.infer<typeof ListModerationQueueResponseSchema>;

export const ModerationDecisionResponseSchema = z.object({ success: z.literal(true) });
export type ModerationDecisionResponse = z.infer<typeof ModerationDecisionResponseSchema>;
